import { analysisStrings } from "@/onboarding/analysis-strings";
import { journeyStrings } from "@/journey/strings";
import { managementStrings } from "@/lib/management-strings";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { brand } from "@/lib/brand";
import { formatMoney } from "@/lib/currency";
import { productDrawerStrings } from "@/lib/product-drawer-strings";
import { companyLanguageOptions, normalizeLanguage, resolveLocalized, type Language } from "@/domain/localization";
import type { Localized } from "@/domain/types";

export type Lang = Language;
export type Dir = "ltr" | "rtl";

export type { Localized } from "@/domain/types";

/** Flat key -> localized string. Keeps the i18n surface explicit and reviewable. */
const dict: Record<string, Localized & { en: string }> = {
  ...journeyStrings,
  ...managementStrings,
  "leads.contactDetails": { en: "Contact details", he: "פרטי קשר" },
  "leads.contactMissing": { en: "Not provided", he: "לא נמסר" },
  "leads.demo": { en: "Demo", he: "הדגמה" },
  "leads.demoHelp": { en: "Test lead. Excluded from analytics.", he: "ליד הדגמה. אינו נכלל בנתונים העסקיים." },
  ...productDrawerStrings,
  "tax.type.vat": { en: "VAT", he: "מע״מ", de: "MwSt." },
  "tax.type.gst": { en: "GST", he: "GST", de: "GST" },
  "tax.type.tax": { en: "tax", he: "מס", de: "Steuer" },
  "disc.tax.before": { en: "Estimated price before tax", he: "מחיר משוער לפני מס", de: "Geschätzter Preis vor Steuern" },
  "disc.tax.partialBefore": { en: "Partial estimate before tax", he: "הערכה חלקית לפני מס", de: "Teilschätzung vor Steuern" },
  "disc.tax.total": { en: "Estimated price including {taxType}", he: "מחיר משוער כולל {taxType}", de: "Geschätzter Preis inkl. {taxType}" },
  "disc.tax.startingTotal": { en: "Estimated starting price including {taxType}", he: "מחיר התחלתי משוער כולל {taxType}", de: "Geschätzter Einstiegspreis inkl. {taxType}" },
  "disc.tax.partialTotal": { en: "Partial estimate including {taxType}", he: "הערכה חלקית כולל {taxType}", de: "Teilschätzung inkl. {taxType}" },
  "disc.tax.excluding": { en: "Excluding tax", he: "לפני מס" },
  "disc.tax.including": { en: "Including {taxType}", he: "כולל {taxType}" },
  "disc.tax.additional": {
    en: "Additional taxes may apply, depending on the project and location.",
    he: "ייתכנו מסים נוספים, בהתאם לפרויקט ולמיקום.",
  },
  "disc.tax.editVat": { en: "All prices exclude tax.", he: "כל המחירים לפני מע״מ" },
  "disc.tax.edit": { en: "All prices exclude tax.", he: "כל המחירים לפני מס" },
  "settings.country": { en: "Business country", he: "מדינת העסק" },
  "settings.taxType": { en: "Tax type", he: "סוג המס" },
  "settings.taxRate": { en: "Tax rate", he: "שיעור המס" },
  "settings.notSet": { en: "Not set", he: "לא הוגדר" },
  "dash.currencyTotals": {
    en: "Monetary totals include {currency} estimates only.",
    he: "הסכומים הכספיים כוללים רק הערכות במטבע {currency}.",
  },
  "brand.name": { en: brand.name, he: brand.name },
  "brand.tagline": {
    en: "Embedded Discovery & Quote Platform",
    he: "פלטפורמת גילוי צרכים והצעות מחיר מוטמעת",
  },

  "nav.dashboard": { en: "Dashboard", he: "סקירה" },
  "nav.pricing": { en: "Pricing Knowledge", he: "ידע תמחור" },
  "nav.knowledge": { en: "Business Knowledge", he: "ידע עסקי" },
  "nav.builder": { en: "Discovery Builder", he: "בונה החוויה" },
  "nav.leads": { en: "Leads", he: "לידים" },
  "nav.settings": { en: "Settings", he: "הגדרות" },
  "nav.section.workspace": { en: "Workspace", he: "סביבת עבודה" },
  "nav.section.intelligence": { en: "Intelligence", he: "מנוע ההמלצה" },
  "nav.viewLive": { en: "View live Journey", he: "צפייה במסע הלקוח הפעיל" },
  "nav.backHome": { en: "Back to overview", he: "חזרה לעמוד הבית" },

  "common.language": { en: "Language", he: "שפה" },
  "common.continue": { en: "Continue", he: "המשך" },
  "common.back": { en: "Back", he: "חזרה" },
  "common.skip": { en: "Skip", he: "דילוג" },
  "common.next": { en: "Next", he: "הבא" },
  "common.save": { en: "Save", he: "שמירה" },
  "common.cancel": { en: "Cancel", he: "ביטול" },
  "common.preview": { en: "Preview", he: "תצוגה מקדימה" },
  "common.publish": { en: "Publish", he: "פרסום" },
  "common.demo": { en: "Demo data", he: "נתוני הדגמה" },
  "common.step": { en: "Step", he: "שלב" },
  "common.of": { en: "of", he: "מתוך" },
  "common.optional": { en: "Optional", he: "לא חובה" },
  "common.review": { en: "Review", he: "בדיקה" },
  "common.approved": { en: "Approved", he: "מאושר" },
  "common.edit": { en: "Edit", he: "עריכה" },
  "common.done": { en: "Done", he: "סיום" },
  "common.close": { en: "Close", he: "סגירה" },

  "onboarding.input.title": {
    en: "Create your initial Qotta",
    he: "יצירת ה-Qotta הראשונית שלך",
  },
  "onboarding.input.body": {
    en: "We'll analyze the business website and create a practical first configuration. Your team can review and publish corrections before going live.",
    he: "ננתח את אתר העסק וניצור תצורה ראשונית מעשית. הצוות שלך יוכל לבדוק ולפרסם תיקונים לפני העלייה לאוויר.",
  },
  "onboarding.input.website": { en: "Business website", he: "אתר העסק" },
  "onboarding.input.submit": {
    en: "Analyze and create configuration",
    he: "ניתוח ויצירת תצורה",
  },
  "onboarding.input.error": {
    en: "Enter a valid public website URL.",
    he: "יש להזין כתובת תקינה של אתר ציבורי.",
  },
  "onboarding.input.includes.profile": {
    en: "Business profile and context",
    he: "פרופיל והקשר עסקי",
  },
  "onboarding.input.includes.offerings": {
    en: "Products, Services, and embedded pricing",
    he: "מוצרים, שירותים ותמחור מובנה",
  },
  "onboarding.input.includes.journey": {
    en: "Knowledge and a usable Discovery Scenario",
    he: "ידע ותרחיש Discovery מוכן לשימוש",
  },
  ...analysisStrings,
  "onboarding.ready.title": { en: "Your Qotta is ready", he: "ה-Qotta שלך מוכנה" },
  "onboarding.ready.withName": {
    en: "We've created a first Qotta experience for",
    he: "יצרנו חוויית Qotta ראשונית עבור",
  },
  "onboarding.ready.withoutName": {
    en: "We've created your first Qotta experience.",
    he: "יצרנו את חוויית Qotta הראשונה שלך.",
  },
  "onboarding.ready.experience": {
    en: "Experience your Qotta",
    he: "לחוות את ה-Qotta שלך",
  },
  "onboarding.ready.dashboard": { en: "Open dashboard", he: "פתיחת לוח הבקרה" },
  "onboarding.notRelevant.title": {
    en: "Qotta may not be the right fit yet",
    he: "ייתכן ש-Qotta עדיין אינה ההתאמה הנכונה",
  },
  "onboarding.notRelevant.body": {
    en: "Based on this website, a guided Qotta experience may not add enough value right now.",
    he: "לפי האתר הזה, ייתכן שחוויית Qotta מודרכת עדיין לא תוסיף מספיק ערך.",
  },
  "onboarding.tryAnother": { en: "Try another website", he: "ניסיון עם אתר אחר" },
  "onboarding.failed.title": {
    en: "We couldn't finish creating your Qotta",
    he: "לא הצלחנו לסיים את יצירת ה-Qotta שלך",
  },
  "onboarding.failed.body": {
    en: "Something interrupted the analysis. You can safely try again.",
    he: "משהו קטע את הניתוח. אפשר לנסות שוב בבטחה.",
  },
  "onboarding.failed.retry": { en: "Try again", he: "ניסיון נוסף" },
  "onboarding.failed.code": { en: "Error code", he: "קוד שגיאה" },
  "onboarding.failed.requestId": { en: "Request ID", he: "מזהה בקשה" },

  "home.title": {
    en: "Turn your website traffic into hot leads",
    he: "הפכו את התנועה באתר שלכם ללידים חמים",
  },
  "home.title.line1": {
    en: "Turn your website traffic",
    he: "הפכו את התנועה באתר שלכם",
  },
  "home.title.line2.prefix": { en: "into", he: "ללידים" },
  "home.title.line2.suffix": { en: "leads", he: "" },
  "home.title.highlight": { en: "hot", he: "חמים" },
  "home.subtitle": {
    en: "Qotta adds an AI-powered discovery experience to your existing website. It helps visitors find the right solutions, get a personalized price estimate, and become qualified leads with their needs already understood.",
    he: "תנו למבקרים את התשובות שבשבילן הגיעו. Qotta מובילה אותם לפתרון הנכון ולהערכת מחיר מותאמת אישית, ובמקביל מעניקה לעסק שלכם ליד איכותי עם כל ההקשר.",
  },
  "home.preview.label": {
    en: "See what Qotta could do for your business",
    he: "ראו את Qotta בפעולה באתר שלכם",
  },
  "home.preview.inputLabel": {
    en: "Your website URL",
    he: "כתובת האתר שלכם",
  },
  "home.preview.placeholder": { en: "yourwebsite.com", he: "yourwebsite.com" },
  "home.preview.cta": {
    en: "Create my Qotta preview",
    he: "צרו תצוגה מקדימה של Qotta",
  },
  "home.backToDashboard": { en: "Back to Dashboard", he: "חזרה ללוח הבקרה" },
  "home.preview.error": {
    en: "Enter a valid website address.",
    he: "הזינו כתובת אתר תקינה.",
  },
  "home.demo.prompt": {
    en: "Just want to explore?",
    he: "רק רוצים להתרשם?",
  },
  "home.demo.link": { en: "View demo", he: "צפו בהדגמה" },
  "home.video.play": {
    en: "Play the Qotta product video",
    he: "הפעילו את סרטון המוצר של Qotta",
  },
  "home.values.label": {
    en: "How Qotta helps both sides",
    he: "איך Qotta עוזרת לשני הצדדים",
  },
  "home.value.fit.title": {
    en: "They discover what fits.",
    he: "הם מוצאים את ההתאמה הנכונה.",
  },
  "home.value.fit.highlight": { en: "fit", he: "ההתאמה" },
  "home.value.fit.body": {
    en: "AI guides visitors through their needs.",
    he: "מונחים אל הפתרון הנכון.",
  },
  "home.value.price.title": {
    en: "They gain clarity.",
    he: "הם מקבלים מחיר.",
  },
  "home.value.price.highlight": { en: "clarity", he: "מחיר" },
  "home.value.price.body": {
    en: "Explore options and optional price estimates.",
    he: "הערכת מחיר מותאמת אישית.",
  },
  "home.value.context.title": {
    en: "You get qualified leads.",
    he: "אתם מקבלים את ההקשר.",
  },
  "home.value.context.highlight": { en: "qualified leads", he: "ההקשר" },
  "home.value.context.body": {
    en: "Real project context, ready for a conversation.",
    he: "ליד שמוכן לשיחה אמיתית.",
  },
  "home.modal.title": {
    en: "See Qotta from both sides",
    he: "ראו את Qotta משני הצדדים",
  },
  "home.modal.subtitle": {
    en: "Experience what your visitor sees and what your business receives.",
    he: "חוו את מה שהמבקרים באתר שלכם רואים ואת מה שהעסק שלכם מקבל.",
  },
  "home.modal.customer.title": {
    en: "Customer experience",
    he: "חוויית הלקוח",
  },
  "home.modal.customer.body": {
    en: "See what your website visitor experiences.",
    he: "ראו את החוויה שמקבלים המבקרים באתר שלכם.",
  },
  "home.modal.business.title": {
    en: "Business dashboard",
    he: "לוח הבקרה העסקי",
  },
  "home.modal.business.body": {
    en: "See the qualified lead and context your business receives.",
    he: "ראו את הליד האיכותי ואת ההקשר שהעסק שלכם מקבל.",
  },
  "home.modal.learnMore": {
    en: "Learn how Qotta turns website visitors into qualified leads",
    he: "איך Qotta הופכת מבקרים באתר ללידים איכותיים",
  },

  "nav.onePager": { en: "One Pager", he: "One Pager" },
  "onePager.nav.home": {
    en: "Back to Qotta home",
    he: "חזרה לעמוד הבית של Qotta",
  },
  "onePager.download": { en: "Download PDF", he: "הורדת PDF" },
  "onePager.hero.title": {
    en: "A smarter price estimate before the form",
    he: "הצעת מחיר חכמה לפני שמשאירים פרטים",
  },
  "onePager.hero.subheadline": {
    en: "Most websites explain what they sell. Very few help buyers understand what it might cost.",
    he: "רוב אתרי האינטרנט יודעים להסביר מה הם מוכרים. מעטים יודעים לעזור ללקוח להבין כמה השירות צפוי לעלות.",
  },
  "onePager.hero.body": {
    en: "Qotta turns passive browsing into guided discovery, giving every visitor a personalized price range before they contact the business.",
    he: "Qotta משנה את חוויית הרכישה ומאפשר לקבל הערכת מחיר מותאמת אישית עוד לפני יצירת קשר עם העסק.",
  },
  "onePager.mockup.question": {
    en: "What kind of kitchen are you planning?",
    he: "איזה מטבח אתם מתכננים?",
  },
  "onePager.mockup.hint": {
    en: "Choose the option that feels closest",
    he: "בחרו את האפשרות שהכי קרובה למה שאתם מחפשים",
  },
  "onePager.mockup.choice.new": { en: "New kitchen", he: "מטבח חדש" },
  "onePager.mockup.choice.country": { en: "Country kitchen", he: "מטבח כפרי" },
  "onePager.mockup.choice.modern": { en: "Modern kitchen", he: "מטבח מודרני" },
  "onePager.mockup.choice.renovation": {
    en: "Kitchen renovation",
    he: "שיפוץ מטבח",
  },
  "onePager.mockup.choice.island": { en: "Kitchen island", he: "אי למטבח" },
  "onePager.mockup.choice.other": { en: "Something else", he: "אחר" },
  "onePager.mockup.resultTitle": {
    en: "Your price estimate",
    he: "הערכת המחיר שלכם",
  },
  "onePager.mockup.price": {
    en: "$68,000 – $86,000",
    he: "$68,000 – $86,000",
  },
  "onePager.mockup.resultNote": {
    en: "Estimated project range",
    he: "טווח משוער לפרויקט",
  },
  "onePager.mockup.includes.planning": { en: "Planning", he: "תכנון המטבח" },
  "onePager.mockup.includes.materials": {
    en: "Materials",
    he: "חומרים ונגרות",
  },
  "onePager.mockup.includes.installation": {
    en: "Installation",
    he: "התקנה מלאה",
  },
  "onePager.mockup.cta": { en: "Get the full proposal", he: "לקבלת הצעה מלאה" },
  "onePager.highlights.eyebrow": { en: "Business impact", he: "השפעה עסקית" },
  "onePager.highlights.title": {
    en: "Less uncertainty. More momentum.",
    he: "פחות חוסר ודאות. יותר תנועה קדימה.",
  },
  "onePager.highlight.conversion": {
    en: "Increase conversion",
    he: "מגדיל המרות",
  },
  "onePager.highlight.conversion.body": {
    en: "Give visitors a useful answer while intent is at its highest.",
    he: "נותן למבקרים תשובה מועילה בדיוק כשהעניין שלהם בשיאו.",
  },
  "onePager.highlight.leads": {
    en: "Win better leads",
    he: "יותר לידים איכותיים",
  },
  "onePager.highlight.leads.body": {
    en: "Every inquiry arrives with context, preferences and buying intent.",
    he: "כל פנייה מגיעה עם הקשר, העדפות וכוונת רכישה ברורה.",
  },
  "onePager.highlight.time": {
    en: "Save the sales team time",
    he: "חוסך זמן לצוות המכירות",
  },
  "onePager.highlight.time.body": {
    en: "Start with a qualified opportunity instead of repeating discovery calls.",
    he: "מתחילים מהזדמנות שכבר הובנה, במקום לחזור שוב על שאלות הבסיס.",
  },
  "onePager.shift.eyebrow": { en: "The shift", he: "השינוי" },
  "onePager.shift.title": {
    en: "From a dead-end form to a buying experience",
    he: "מטופס ללא מוצא לחוויית רכישה אמיתית",
  },
  "onePager.problem.title": { en: "The problem", he: "הבעיה" },
  "onePager.problem.body": {
    en: "Most websites selling tailored products or services send every buyer to the same ‘leave your details’ form. The result is uncertainty and fewer qualified inquiries.",
    he: "רוב האתרים שמוכרים שירותים או מוצרים בהתאמה אישית שולחים את הלקוחות לטופס „השאירו פרטים”. התוצאה היא חוסר ודאות ופחות פניות איכותיות.",
  },
  "onePager.opportunity.title": { en: "The opportunity", he: "ההזדמנות" },
  "onePager.opportunity.body": {
    en: "Buyers rarely need an exact quote on the spot. They need a credible range that tells them whether the next step makes sense.",
    he: "רוב הלקוחות אינם מחפשים מחיר מדויק. הם רוצים להבין סדר גודל כדי לדעת האם כדאי להמשיך.",
  },
  "onePager.solution.title": { en: "The solution", he: "הפתרון" },
  "onePager.solution.body": {
    en: "Qotta guides each buyer through a few simple choices, understands what matters, and delivers a personalized estimate at the right moment.",
    he: "Qotta מוביל את הלקוח בתהליך גילוי חכם המבוסס על בחירות פשוטות, ובסיום מספק הערכת מחיר מותאמת אישית.",
  },
  "onePager.how.eyebrow": { en: "How it works", he: "איך זה עובד" },
  "onePager.how.title": {
    en: "From first click to qualified lead",
    he: "מהקליק הראשון ועד לליד איכותי",
  },
  "onePager.step.domain": { en: "Choose a category", he: "הלקוח בוחר תחום" },
  "onePager.step.cards": {
    en: "Select from visual cards",
    he: "בחירה מתוך כרטיסים",
  },
  "onePager.step.understand": {
    en: "Qotta understands the need",
    he: "Qotta מבינה את הצורך",
  },
  "onePager.step.questions": {
    en: "Answer focused questions",
    he: "שאלות ממוקדות",
  },
  "onePager.step.estimate": {
    en: "Receive a personal estimate",
    he: "הערכת מחיר אישית",
  },
  "onePager.step.lead": {
    en: "Business gets a qualified lead",
    he: "ליד איכותי לעסק",
  },
  "onePager.benefits.eyebrow": {
    en: "Built for both sides",
    he: "ערך לשני הצדדים",
  },
  "onePager.benefits.title": {
    en: "A better decision for buyers. A better opportunity for business.",
    he: "החלטה טובה יותר ללקוח. הזדמנות טובה יותר לעסק.",
  },
  "onePager.benefit.customer.eyebrow": { en: "For buyers", he: "ללקוחות" },
  "onePager.benefit.customer.title": {
    en: "Buy with clarity",
    he: "יתרונות ללקוחות",
  },
  "onePager.benefit.customer.certainty": {
    en: "Less uncertainty",
    he: "פחות חוסר ודאות",
  },
  "onePager.benefit.customer.range": {
    en: "A useful price range",
    he: "הבנת סדר גודל המחיר",
  },
  "onePager.benefit.customer.simple": {
    en: "A short, simple process",
    he: "תהליך קצר ופשוט",
  },
  "onePager.benefit.customer.decision": {
    en: "A more confident decision",
    he: "החלטה טובה יותר",
  },
  "onePager.benefit.business.eyebrow": { en: "For businesses", he: "לעסק" },
  "onePager.benefit.business.title": {
    en: "Sell with context",
    he: "יתרונות לעסק",
  },
  "onePager.benefit.business.leads": {
    en: "More qualified leads",
    he: "יותר לידים איכותיים",
  },
  "onePager.benefit.business.conversion": {
    en: "Higher conversion",
    he: "יותר המרות",
  },
  "onePager.benefit.business.time": {
    en: "Less time wasted",
    he: "פחות זמן מבוזבז",
  },
  "onePager.benefit.business.cycle": {
    en: "A shorter sales cycle",
    he: "מחזור מכירה קצר יותר",
  },
  "onePager.whyNow.eyebrow": { en: "The moment", he: "העיתוי" },
  "onePager.whyNow.title": { en: "Why now?", he: "למה עכשיו?" },
  "onePager.whyNow.body1": {
    en: "AI is reshaping how businesses operate. Yet the buying experience on most websites has barely moved.",
    he: "מהפכת ה-AI משנה את הדרך שבה עסקים עובדים. אבל חוויית הרכישה ברוב אתרי האינטרנט כמעט שלא השתנתה.",
  },
  "onePager.whyNow.body2": {
    en: "Qotta brings intelligence to the moment that matters: when a buyer wants to understand what the service is likely to cost.",
    he: "Qotta מביא בינה מלאכותית בדיוק לנקודה שבה הלקוח רוצה להבין כמה השירות צפוי לעלות.",
  },
  "onePager.footer.tagline": {
    en: "AI-guided discovery that turns interest into an informed buying decision.",
    he: "חוויית גילוי חכמה שהופכת עניין להחלטת רכישה מבוססת.",
  },
  "onePager.footer.qrLabel": {
    en: "Placeholder QR code for Qotta.co",
    he: "קוד QR זמני לאתר Qotta.co",
  },

  "dash.title": { en: "Overview", he: "סקירה כללית" },
  "dash.subtitle": {
    en: "Everything happening across your discovery experience.",
    he: "כל מה שקורה בחוויית הגילוי שלכם.",
  },
  "dash.kpi.discoveries": { en: "Discoveries", he: "תהליכי גילוי" },
  "dash.kpi.results": { en: "Discovery results", he: "תוצאות גילוי" },
  "dash.kpi.leads": { en: "Qualified leads", he: "לידים מוסמכים" },
  "dash.kpi.value": { en: "Pipeline value", he: "שווי צנרת" },
  "dash.conversion": { en: "Conversion overview", he: "סקירת המרות" },
  "dash.conversionSub": { en: "Last 7 days", he: "7 הימים האחרונים" },
  "dash.funnel": { en: "Journey funnel", he: "משפך המסע" },
  "dash.recentLeads": { en: "Recent leads", he: "לידים אחרונים" },
  "dash.recentConversations": { en: "Recent Discoveries", he: "תהליכי גילוי אחרונים" },
  "dash.activity": { en: "Recent activity", he: "פעילות אחרונה" },
  "dash.viewAll": { en: "View all", he: "הצג הכל" },
  "dash.avgEstimate": { en: "Avg. estimate", he: "הערכה ממוצעת" },
  "dash.avgTime": { en: "Avg. time to estimate", he: "זמן ממוצע להערכה" },
  "dash.insight.eyebrow": {
    en: "Discovery intelligence",
    he: "תובנות Discovery",
  },
  "dash.insight.title": {
    en: "Your strongest Discoveries are reaching a result faster",
    he: "הסשנים החזקים ביותר מגיעים להערכה מהר יותר",
  },
  "dash.insight.body": {
    en: "The last website sync improved pricing coverage. Two high-impact gaps still affect complex projects.",
    he: "סנכרון האתר האחרון שיפר את כיסוי התמחור. שני פערים משמעותיים עדיין משפיעים על פרויקטים מורכבים.",
  },
  "dash.insight.quality": { en: "estimate readiness", he: "מוכנות להערכה" },
  "dash.insight.action": { en: "Review gaps", he: "בדיקת פערים" },

  "pricing.title": { en: "Pricing Knowledge", he: "ידע תמחור" },
  "pricing.subtitle": {
    en: "Teach the platform how you price. Estimates are enough. Precision is not required.",
    he: "לימדו את המערכת איך אתם מתמחרים. הערכות מספיקות. אין צורך בדיוק מוחלט.",
  },
  "pricing.tab.model": { en: "Products & services", he: "מוצרים ושירותים" },
  "pricing.tab.upload": { en: "Upload documents", he: "העלאת מסמכים" },
  "pricing.tab.website": { en: "Website import", he: "ייבוא מאתר" },
  "pricing.coverage": { en: "Pricing coverage", he: "כיסוי תמחורי" },
  "pricing.coverageNote": {
    en: "Enough knowledge to estimate 9 of every 10 incoming projects.",
    he: "מספיק ידע כדי להעריך 9 מכל 10 פרויקטים נכנסים.",
  },
  "pricing.dropTitle": {
    en: "Drop price lists, catalogs or past quotes",
    he: "גררו מחירונים, קטלוגים או הצעות קודמות",
  },
  "pricing.dropSub": {
    en: "Excel, CSV or PDF · up to 20MB",
    he: "אקסל, CSV או PDF · עד 20MB",
  },
  "pricing.browse": { en: "Browse files", he: "בחירת קבצים" },
  "pricing.interpreted": {
    en: "How we read this file",
    he: "כך פירשנו את הקובץ",
  },
  "pricing.rowsFound": {
    en: "priced items detected",
    he: "פריטים מתומחרים זוהו",
  },
  "pricing.urlLabel": { en: "Your website address", he: "כתובת האתר שלכם" },
  "pricing.import": { en: "Import", he: "ייבוא" },
  "pricing.importing": {
    en: "Reading your website…",
    he: "קוראים את האתר שלכם…",
  },
  "pricing.importManaged": {
    en: "Initial website analysis is managed during Qotta setup. This view shows the website knowledge currently in your workspace.",
    he: "ניתוח האתר הראשוני מתבצע בזמן הגדרת Qotta. תצוגה זו מציגה את הידע מהאתר שנמצא כעת בסביבת העבודה.",
  },
  "pricing.discovered": { en: "Discovered on your site", he: "נמצא באתר שלכם" },
  "pricing.suggestedModel": {
    en: "Product and Service pricing",
    he: "מודל תמחור מוצע",
  },
  "pricing.suggestedModelSub": {
    en: "Built from your site, your documents and how similar businesses price.",
    he: "נבנה מהאתר שלכם, מהמסמכים ומהאופן שבו עסקים דומים מתמחרים.",
  },
  "pricing.approveAll": { en: "Approve all", he: "אישור הכל" },
  "pricing.typicalRange": { en: "Typical range", he: "טווח אופייני" },
  "pricing.items": { en: "items", he: "פריטים" },
  "pricing.addOns": { en: "Add-ons & accessories", he: "תוספות ואביזרים" },
  "pricing.installation": { en: "Installation types", he: "סוגי התקנה" },
  "pricing.regenerate": { en: "Regenerate suggestion", he: "יצירת הצעה מחדש" },

  "kb.title": { en: "Business Knowledge", he: "ידע עסקי" },
  "kb.subtitle": {
    en: "Context that shapes recommendations: policies, answers and reference material.",
    he: "ההקשר שמעצב את ההמלצות: מדיניות, תשובות וחומרי רקע.",
  },
  "kb.tab.faq": { en: "FAQ", he: "שאלות נפוצות" },
  "kb.tab.docs": { en: "Documents", he: "מסמכים" },
  "kb.tab.policies": { en: "Policies", he: "מדיניות" },
  "kb.tab.site": { en: "Website", he: "אתר" },
  "kb.addFaq": { en: "Add question", he: "הוספת שאלה" },
  "kb.lastSynced": { en: "Last synced", he: "סונכרן לאחרונה" },
  "kb.resync": { en: "Re-sync website", he: "סנכרון מחדש" },
  "kb.pagesRead": { en: "pages read", he: "עמודים נקראו" },
  "kb.quality.eyebrow": { en: "Knowledge intelligence", he: "תובנות ידע" },
  "kb.quality.title": {
    en: "Your knowledge base is ready for most visitor questions",
    he: "בסיס הידע מוכן לרוב שאלות המבקרים",
  },
  "kb.quality.body": {
    en: "Add service limitations and policy exceptions to reduce uncertainty in edge-case recommendations.",
    he: "הוסיפו מגבלות שירות וחריגים למדיניות כדי לצמצם אי-ודאות בהמלצות למקרים חריגים.",
  },
  "kb.quality.label": { en: "knowledge quality", he: "איכות הידע" },

  "builder.title": { en: "Discovery Builder", he: "בונה חוויית הגילוי" },
  "builder.subtitle": {
    en: "Seven short steps. Live in minutes.",
    he: "שבעה שלבים קצרים. באוויר תוך דקות.",
  },
  "builder.step.welcome": { en: "Welcome", he: "פתיחה" },
  "builder.step.business": { en: "Business type", he: "סוג העסק" },
  "builder.step.strategy": { en: "Pricing strategy", he: "אסטרטגיית תמחור" },
  "builder.step.scenario": {
    en: "Discovery Scenario",
    he: "חוויית הגילוי",
  },
  "builder.step.handoff": { en: "Human handoff", he: "מעבר לנציג" },
  "builder.step.preview": { en: "Preview", he: "תצוגה מקדימה" },
  "builder.step.publish": { en: "Publish", he: "פרסום" },
  "builder.welcomeTitle": {
    en: "Let's set up your Discovery Scenario",
    he: "בואו נגדיר את חוויית הגילוי שלכם",
  },
  "builder.welcomeBody": {
    en: "We already read your website and your price lists. The next steps only confirm how you want customers to be guided.",
    he: "כבר קראנו את האתר ואת המחירונים שלכם. השלבים הבאים רק מאשרים איך תרצו להנחות את הלקוחות.",
  },
  "builder.businessBody": {
    en: "What kind of work do you sell?",
    he: "איזה סוג עבודה אתם מוכרים?",
  },
  "builder.strategyBody": {
    en: "How should prices be shown?",
    he: "איך להציג את המחירים?",
  },
  "builder.scenarioBody": {
    en: "How much should we ask before estimating?",
    he: "כמה לשאול לפני הערכת מחיר?",
  },
  "builder.handoffBody": {
    en: "When should a person step in?",
    he: "מתי נציג אנושי נכנס לתמונה?",
  },
  "builder.previewBody": {
    en: "This is exactly what your visitors will see.",
    he: "זה בדיוק מה שהמבקרים שלכם יראו.",
  },
  "builder.openPreview": {
    en: "Open live Discovery",
    he: "פתיחת החוויה המלאה",
  },
  "builder.publishTitle": { en: "Ready to go live", he: "מוכן לעלייה לאוויר" },
  "builder.publishBody": {
    en: "Paste one line into your site and the Discovery appears on every page.",
    he: "הדביקו שורה אחת באתר והחוויה תופיע בכל עמוד.",
  },
  "builder.copied": { en: "Snippet copied", he: "הקוד הועתק" },
  "builder.copy": { en: "Copy snippet", he: "העתקת קוד" },
  "builder.published": { en: "Changes published", he: "השינויים פורסמו" },

  "leads.title": { en: "Leads", he: "לידים" },
  "leads.subtitle": {
    en: "Every lead arrives with the full story behind it.",
    he: "כל ליד מגיע עם כל הסיפור מאחוריו.",
  },
  "leads.search": { en: "Search leads", he: "חיפוש לידים" },
  "leads.total": { en: "Total Leads", he: "סך כל הלידים" },
  "leads.recentLimit": { en: "Newest 500 records; search and filters apply to loaded leads. Summary covers all leads.", he: "מוצגים 500 הלידים האחרונים. החיפוש והסינון חלים עליהם; הסיכום כולל את כל הלידים." },
  "leads.all": { en: "All", he: "הכל" },
  "leads.name": { en: "Customer", he: "לקוח" },
  "leads.solution": { en: "Recommended solution", he: "פתרון מומלץ" },
  "leads.estimate": { en: "Estimate", he: "הערכה" },
  "leads.status": { en: "Status", he: "סטטוס" },
  "leads.date": { en: "Created", he: "נוצר" },
  "leads.summary": { en: "Conversation summary", he: "סיכום השיחה" },
  "leads.intelligentSummary": {
    en: "AI discovery summary",
    he: "סיכום גילוי צרכים מבוסס AI",
  },
  "leads.answers": { en: "Customer answers", he: "תשובות הלקוח" },
  "leads.timeline": { en: "Timeline", he: "ציר זמן" },
  "leads.empty": {
    en: "Select a lead to see the full picture",
    he: "בחרו ליד כדי לראות את התמונה המלאה",
  },
  "leads.confidence": { en: "Confidence", he: "רמת ודאות" },
  "status.new.description": { en: "Not yet handled.", he: "טרם טופל." },
  "status.contacted.description": { en: "Outreach or initial contact attempt.", he: "פנייה או ניסיון ראשון ליצירת קשר." },
  "status.interested.description": { en: "Positive response or meaningful engagement.", he: "תגובה חיובית או התעניינות משמעותית." },
  "status.quoted.description": { en: "A commercial proposal was sent.", he: "נשלחה הצעה מסחרית." },
  "status.won.description": { en: "The business relationship has started, including Design Partners and paying customers.", he: "החל קשר עסקי, לרבות שותפי פיתוח ולקוחות משלמים." },
  "status.closed.description": { en: "No further follow-up planned.", he: "לא מתוכנן המשך מעקב." },
  "status.interested": { en: "Interested", he: "מתעניין" },
  "status.closed": { en: "Closed", he: "סגור" },
  "leads.add": { en: "Add Lead", he: "הוספת ליד" },
  "leads.contact.name": { en: "Contact name", he: "שם איש קשר" },
  "leads.contact.business": { en: "Business name", he: "שם העסק" },
  "leads.contact.email": { en: "Email", he: "אימייל" },
  "leads.contact.phone": { en: "Phone", he: "טלפון" },
  "leads.contact.website": { en: "Website", he: "אתר" },
  "leads.edit": { en: "Edit", he: "עריכה" },
  "leads.save": { en: "Save", he: "שמירה" },
  "leads.cancel": { en: "Cancel", he: "ביטול" },
  "leads.saving": { en: "Saving...", he: "שומר…" },
  "leads.saved": { en: "Saved", he: "נשמר" },
  "leads.saveError": {
    en: "Could not save. Check the fields and try again.",
    he: "לא ניתן לשמור. בדקו את השדות ונסו שוב.",
  },
  "leads.origin.manual": { en: "Manual lead", he: "ליד ידני" },
  "leads.origin.discovery": { en: "Automated lead", he: "ליד אוטומטי" },
  "status.new": { en: "New", he: "חדש" },
  "status.contacted": { en: "Contacted", he: "נוצר קשר" },
  "status.quoted": { en: "Quoted", he: "נשלחה הצעה" },
  "status.won": { en: "Won", he: "נסגר" },

  "settings.title": { en: "Settings", he: "הגדרות" },
  "settings.subtitle": {
    en: "Workspace, branding and handoff preferences.",
    he: "סביבת עבודה, מיתוג והעדפות מעבר לנציג.",
  },
  "settings.discardChanges": { en: "Discard Changes", he: "ביטול שינויים" },
  "settings.general": { en: "General", he: "כללי" },
  "settings.branding": { en: "Branding", he: "מיתוג" },
  "settings.notifications": { en: "Notifications", he: "התראות" },
  "settings.businessName": { en: "Business name", he: "שם העסק" },
  "settings.website": { en: "Website", he: "אתר" },
  "settings.currency": { en: "Currency", he: "מטבע" },
  "settings.brandColor": { en: "Brand color", he: "צבע מותג" },
  "settings.secondaryColor": { en: "Secondary color", he: "צבע משני" },
  "settings.fontFamily": { en: "Font family", he: "גופן" },
  "settings.cornerRadius": { en: "Corner radius", he: "עיגול פינות" },
  "settings.buttonStyle": { en: "Button style", he: "סגנון כפתור" },
  "settings.heroStyle": { en: "Hero style", he: "סגנון פתיח" },
  "settings.leadEmail": { en: "Lead notification email", he: "כתובת מייל לקבלת לידים" },
  "settings.leadEmailDefault": { en: "Default: {contactEmail}", he: "ברירת מחדל: {contactEmail}" },
  "settings.invalidLeadEmail": { en: "Enter a valid lead notification email address.", he: "יש להזין כתובת מייל תקינה לקבלת לידים." },
  "settings.emailAlerts": {
    en: "Email me on every new lead",
    he: "שלחו לי מייל על כל ליד חדש",
  },
  "settings.dailyDigest": { en: "Daily summary", he: "סיכום יומי" },
  "settings.handoffAlerts": {
    en: "Alert when a customer asks for a person",
    he: "התראה כשלקוח מבקש נציג",
  },

  "disc.hero.eyebrow": { en: "Discovery", he: "גילוי צרכים" },
  "disc.identity": { en: "Discovery", he: "גילוי צרכים" },
  "disc.brand.supporting": {
    en: "Get a personalized recommendation and price estimate in a few minutes.",
    he: "קבלו המלצה מותאמת אישית והערכת מחיר בתוך כמה דקות.",
  },
  "disc.hero.title": {
    en: "Plan your project with a realistic price range",
    he: "תכננו את הפרויקט שלכם עם טווח מחירים ריאלי",
  },
  "disc.hero.subtitle": {
    en: "Answer five helpful questions and see a personalized recommendation with a preliminary price range. No phone number or email until you want to continue.",
    he: "ענו על חמש שאלות מועילות וקבלו המלצה מותאמת אישית עם טווח מחירים ראשוני. אין צורך במספר טלפון או אימייל עד שתרצו להמשיך.",
  },
  "disc.hero.start": { en: "Start", he: "התחלה" },
  "disc.hero.time": { en: "About 45 seconds", he: "בערך 45 שניות" },
  "disc.hero.point1": {
    en: "A realistic price range",
    he: "טווח מחירים ריאלי",
  },
  "disc.hero.point2": {
    en: "A solution matched to your needs",
    he: "פתרון שמתאים לצרכים שלכם",
  },
  "disc.hero.point3": {
    en: "No contact details required",
    he: "בלי למסור פרטי קשר",
  },
  "disc.describe": {
    en: "Describe your project in your own words",
    he: "תארו את הפרויקט במילים שלכם",
  },
  "disc.describePlaceholder": {
    en: "e.g. the service you need, your priorities, preferences, and timeline",
    he: "למשל השירות שאתם צריכים, מה חשוב לכם, ההעדפות שלכם ולוח הזמנים",
  },
  "disc.understood": {
    en: "Got it. We used that context in your answers",
    he: "הבנתי. השתמשנו בהקשר הזה בתשובות שלכם",
  },
  "disc.summary": { en: "Your project", he: "הפרויקט שלך" },
  "disc.summaryEmpty": {
    en: "Your answers will build this summary.",
    he: "התשובות שלך יבנו את הסיכום הזה.",
  },
  "disc.understand": { en: "What we understand", he: "מה שהבנו" },
  "disc.recommended": { en: "Recommended solution", he: "פתרון מומלץ" },
  "disc.from": { en: "From", he: "החל מ-" },
  "disc.startingPrice": { en: "Estimated starting price", he: "מחיר התחלתי משוער" },
  "disc.fixedPrice": { en: "Estimated price", he: "מחיר משוער" },
  "disc.result.startingTitle": { en: "Here's your estimated starting price", he: "הנה המחיר ההתחלתי המשוער לפרויקט שלך" },
  "disc.result.fixedTitle": { en: "Here's your estimated project price", he: "הנה המחיר המשוער לפרויקט שלך" },
  "disc.partialEstimate": { en: "Partial estimate", he: "הערכה חלקית" },
  "disc.partialEstimateNote": { en: "This is a partial estimate, not the total project price. Not included: {offerings}. Separate pricing is required.", he: "זוהי הערכה חלקית ולא המחיר הכולל של הפרויקט. לא כלול בהערכה: {offerings}. נדרש תמחור נפרד." },
  "disc.quoteRequired": { en: "Personalized quote required", he: "נדרשת הצעת מחיר אישית" },
  "disc.detailsReady": { en: "Your project details are ready", he: "פרטי הפרויקט שלך מוכנים" },
  "disc.reviewDetails": { en: "Review project details", he: "לעיון בפרטי הפרויקט" },
  "disc.estimate": { en: "Current estimate", he: "הערכה נוכחית" },
  "disc.confidence": { en: "Confidence", he: "רמת ודאות" },
  "disc.characteristics": { en: "Key characteristics", he: "מאפייני הפרויקט" },
  "disc.enoughInfo": {
    en: "We have enough to estimate. You can finish now.",
    he: "יש לנו מספיק כדי להעריך. אפשר לסיים עכשיו.",
  },
  "disc.seeEstimate": { en: "See my estimate", he: "להערכה שלי" },
  "disc.result.eyebrow": { en: "Your estimate", he: "ההערכה שלך" },
  "disc.result.title": {
    en: "Here's your estimated project range",
    he: "הנה טווח המחירים המשוער לפרויקט שלכם",
  },
  "disc.result.range": { en: "Estimated range", he: "טווח משוער" },
  "disc.result.includes": { en: "What's driving your estimate", he: "מה משפיע על ההערכה" },
  "disc.result.upgrades": { en: "Key factors and assumptions", he: "גורמים חשובים והשערות" },
  "disc.result.addUpgrade": { en: "Add", he: "הוספה" },
  "disc.result.added": { en: "Added", he: "נוסף" },
  "disc.customerNotes.title": { en: "Anything you'd like us to know?", he: "יש משהו נוסף שחשוב לך שנדע?" },
  "disc.customerNotes.description": { en: "Share your preferences, a different direction you have in mind, or anything about your project we may have missed. You can also mention a budget if you'd like.", he: "אפשר לשתף העדפות, כיוון אחר שחשבת עליו או פרטים על הפרויקט שאולי פספסנו. אפשר גם לציין תקציב, אם רוצים." },
  "disc.customerNotes.placeholder": { en: "Tell us more about what you have in mind... (Optional)", he: "אפשר לספר לנו עוד על מה שחשבת... (לא חובה)" },
  "disc.customerNotes.helper": { en: "Optional, shared with the business", he: "לא חובה, המידע יועבר לעסק" },
  "leads.customerNotes": { en: "Customer's additional preferences", he: "העדפות נוספות של הלקוח" },
  "disc.result.fullQuote": { en: "Continue with a specialist", he: "המשך עם מומחה" },
  "disc.result.improve": { en: "Improve the estimate", he: "לשיפור ההערכה" },
  "disc.result.improveHint": {
    en: "Answer 2 more questions to narrow this range.",
    he: "שתי שאלות נוספות יצמצמו את הטווח.",
  },
  "disc.result.validity": {
    en: "Estimate based on the current live Discovery Scenario",
    he: "הערכה מבוססת על חוויית הגילוי הנוכחית",
  },
  "disc.contact.title": {
    en: "Continue with {businessName}",
    he: "ממשיכים עם {businessName}",
  },
  "disc.contact.body": {
    en: "Share your details and project information with {businessName}. A representative will review your Discovery and follow up with you.",
    he: "שתפו את פרטי הקשר ואת המידע על הפרויקט שלכם עם {businessName}. נציג מטעם העסק יעבור על תהליך גילוי הצרכים שלכם ויצור איתכם קשר.",
  },
  "disc.contact.name": { en: "Full name", he: "שם מלא" },
  "disc.contact.phone": { en: "Phone", he: "טלפון" },
  "disc.contact.email": { en: "Email", he: "אימייל" },
  "disc.contact.saving": { en: "Submitting...", he: "שולחים..." },
  "disc.contact.retry": { en: "Retry notification", he: "ניסיון שליחת הודעה נוסף" },
  "disc.contact.saved": { en: "Your details are saved. Retry to confirm the notification request without creating another lead.", he: "הפרטים נשמרו. נסו שוב לאשר את בקשת ההודעה, ללא יצירת פנייה נוספת." },
  "disc.contact.failed": { en: "Submission was not confirmed. Please retry.", he: "השליחה לא אושרה. נא לנסות שוב." },
  "disc.contact.send": {
    en: "Connect me with the business",
    he: "חברו אותי לעסק",
  },
  "disc.contact.privacy": {
    en: "By continuing, you agree that your contact details and Discovery information will be shared with {businessName} for follow-up. See Qotta's {privacyPolicy}.",
    he: "בהמשך, אתם מסכימים שפרטי הקשר והמידע מתהליך גילוי הצרכים שלכם ישותפו עם {businessName} לצורך יצירת קשר. ראו את {privacyPolicy} של Qotta.",
  },
  "disc.contact.privacyPolicy": { en: "Privacy Policy", he: "מדיניות הפרטיות" },
  "disc.contact.done": { en: "On its way", he: "בדרך אליכם" },
  "disc.contact.doneBody": {
    en: "A business representative will review your project and follow up within one business day.",
    he: "נציג מהעסק יבדוק את הפרויקט שלכם ויחזור אליכם תוך יום עסקים אחד.",
  },
  "disc.restart": { en: "Start over", he: "התחלה מחדש" },
  "disc.exit": { en: "Exit demo", he: "יציאה מההדגמה" },
  "disc.progress": { en: "Progress", he: "התקדמות" },
};

type I18nValue = {
  lang: Lang;
  languageOptions: Lang[];
  dir: Dir;
  setLang: (l: Lang) => void;
  toggleLang: () => void;
  t: (key: string) => string;
  tl: (value: Localized | undefined) => string;
  formatCurrency: (value: number, currency: string) => string;
  formatNumber: (value: number) => string;
};

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children, language, companyKey = "default" }: { children: ReactNode; language?: Lang; companyKey?: string }) {
  const primaryLanguage = normalizeLanguage(language) ?? "en";
  const selectionKey = `${companyKey}:${primaryLanguage}`;
  const [selection, setSelection] = useState<{ key: string; language: Lang } | null>(null);
  // Keep the override in this company context; a new company always starts at its default.
  const lang = selection?.key === selectionKey ? selection.language : primaryLanguage;

  const dir: Dir = lang === "he" ? "rtl" : "ltr";

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = dir;
  }, [lang, dir]);

  const setLang = useCallback((l: Lang) => {
    if (companyLanguageOptions(primaryLanguage).includes(l)) {
      setSelection({ key: selectionKey, language: l });
    }
  }, [primaryLanguage, selectionKey]);

  const value = useMemo<I18nValue>(() => {
    const t = (key: string) => dict[key]?.[lang] ?? dict[key]?.en ?? key;
    const tl = (v: Localized | undefined) => resolveLocalized(v, lang);
    const nf = new Intl.NumberFormat(lang === "he" ? "he-IL" : "en-US");
    return {
      lang,
      languageOptions: companyLanguageOptions(primaryLanguage),
      dir,
      setLang,
      toggleLang: () => setLang(lang === "en" ? primaryLanguage : "en"),
      t,
      tl,
      formatCurrency: (v: number, currency: string) =>
        formatMoney(v, currency, lang === "he" ? "he-IL" : "en-US"),
      formatNumber: (v: number) => nf.format(v),
    };
  }, [lang, dir, setLang, primaryLanguage]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside I18nProvider");
  return ctx;
}
